import React from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Loader2, XCircle, Home } from 'lucide-react';
import { invoices as invoicesApi } from '@/lib/api';

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 15; // 30 seconds

export function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sessionId = params.get('session_id');
  const [status, setStatus] = React.useState('polling');
  const [details, setDetails] = React.useState(null);
  const pollsRef = React.useRef(0);

  React.useEffect(() => {
    if (!sessionId) { setStatus('missing'); return; }
    let timer;
    const poll = async () => {
      try {
        const s = await invoicesApi.status(sessionId);
        setDetails(s);
        if (s.payment_status === 'paid') { setStatus('paid'); return; }
        if (s.status === 'expired' || s.status === 'failed') { setStatus('failed'); return; }
        pollsRef.current += 1;
        if (pollsRef.current >= MAX_POLLS) { setStatus('timeout'); return; }
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        // 404 during first few polls just means the webhook is still landing.
        // Keep polling until MAX_POLLS, then show timeout — never flash "failed" prematurely.
        pollsRef.current += 1;
        if (pollsRef.current >= MAX_POLLS) { setStatus('timeout'); return; }
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      }
    };
    poll();
    return () => timer && clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F4F7FA] p-6" data-testid="payment-success-page">
      <Card className="w-full max-w-lg border-slate-200 shadow-sm">
        <CardContent className="p-8 text-center">
          {status === 'polling' && (
            <>
              <Loader2 className="mx-auto h-10 w-10 animate-spin text-[#2ECC9A]" />
              <h1 className="mt-4 font-serif text-2xl text-[#1A3A5C]">Confirming your payment…</h1>
              <p className="mt-2 text-sm text-slate-500">This usually takes a couple of seconds.</p>
            </>
          )}
          {status === 'paid' && (
            <>
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100">
                <CheckCircle2 className="h-8 w-8 text-emerald-600" />
              </div>
              <h1 className="mt-4 font-serif text-2xl text-[#1A3A5C]">Payment received</h1>
              <p className="mt-2 text-sm text-slate-500">Thanks — your invoice has been marked paid and posted to your matter&rsquo;s trust ledger.</p>
              <Button
                data-testid="payment-back-to-portal"
                onClick={() => nav('/portal')}
                className="mt-6 gap-1.5 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90"
              >
                <Home className="h-4 w-4" /> Back to your portal
              </Button>
            </>
          )}
          {(status === 'failed' || status === 'error') && (
            <>
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-red-100">
                <XCircle className="h-8 w-8 text-red-600" />
              </div>
              <h1 className="mt-4 font-serif text-2xl text-[#1A3A5C]">We couldn&rsquo;t confirm your payment</h1>
              <p className="mt-2 text-sm text-slate-500">If you were charged, please contact your migration agent — no action is required from you.</p>
              <Button onClick={() => nav('/portal')} variant="outline" className="mt-6">Back to portal</Button>
            </>
          )}
          {status === 'timeout' && (
            <>
              <Loader2 className="mx-auto h-10 w-10 text-amber-500" />
              <h1 className="mt-4 font-serif text-2xl text-[#1A3A5C]">Still processing…</h1>
              <p className="mt-2 text-sm text-slate-500">Your payment is going through Stripe. Refresh in a minute to see it as paid.</p>
              <Button onClick={() => nav('/portal')} variant="outline" className="mt-6">Back to portal</Button>
            </>
          )}
          {status === 'missing' && (
            <>
              <p className="text-sm text-slate-500">No session reference was returned.</p>
              <Link to="/portal" className="text-sm text-[#1A3A5C] underline">Return to portal</Link>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export function PaymentCancelPage() {
  const nav = useNavigate();
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F4F7FA] p-6" data-testid="payment-cancel-page">
      <Card className="w-full max-w-lg border-slate-200 shadow-sm">
        <CardContent className="p-8 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-slate-100">
            <XCircle className="h-8 w-8 text-slate-500" />
          </div>
          <h1 className="mt-4 font-serif text-2xl text-[#1A3A5C]">Payment cancelled</h1>
          <p className="mt-2 text-sm text-slate-500">No charge was made. You can retry any time from your portal.</p>
          <Button onClick={() => nav('/portal')} className="mt-6 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90">Back to portal</Button>
        </CardContent>
      </Card>
    </div>
  );
}
